import React, { useState, useCallback, useMemo, useRef } from 'react';
import { ScrollView, Keyboard, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BlurTargetView } from 'expo-blur';
import { useAuth } from '~/components/providers/AuthProvider';
import dayjs from '@/i18n/dayjs-config';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Heading } from '@/components/gluestack/heading';
import { Card } from '@/components/gluestack/card';
import { Button, ButtonText, ButtonSpinner } from '@/components/gluestack/button';
import { Input, InputField, InputSlot, InputIcon } from '@/components/gluestack/input';
import { CloseIcon } from '@/components/gluestack/icon';
import { Divider } from '@/components/gluestack/divider';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { SettingsItem } from '@/components/ui/SettingsItem';
import { ContextSheet } from '@/components/ui/ContextSheet';
import MultiSelectSheet from '@/components/ui/MultiSelectSheet';
import SingleSelectSheet from '@/components/ui/SingleSelectSheet';
import DatePickerSheet from '@/components/ui/DatePickerSheet';
import TextEditSheet from '@/components/ui/TextEditSheet';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { MenuSelectButton } from '@/components/ui/MenuSelectButton';
import { SafeBlurView } from '@/components/ui/SafeBlurView';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { SocialTagIcon } from '~/components/domain/common/SocialTagIcon';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useCommunityTranslations } from '@/hooks/useCommunityTranslations';
import { useTheme } from '@/components/providers/ThemeProvider';
import { AccountService } from '~/services/domain/AccountService';
import {
  IMemberProfileEdit,
  IProfileOptions,
  HEIGHT_OPTIONS,
  getHeightDisplay,
} from '~/models/MemberProfileEdit';
import {
  EDIT_PROFILE_SECTIONS,
  EDIT_PROFILE_MULTI_SELECT_ICON_BY_FIELD,
  EDIT_PROFILE_BASIC_ICONS,
  MULTI_SELECT_LABEL_KEY_BY_FIELD,
  PROFILE_EDIT_TRANSLATION_MAP,
  DEFAULT_MULTI_SELECT_ICON,
  isSingleSelectField,
  isFieldUnsupported,
  type TagField,
} from '~/components/domain/profile/editProfileConfig';
import {
  useMeasurementPreferences,
  weightFromKg,
  weightToKg,
  WEIGHT_MIN_KG,
  WEIGHT_MAX_KG,
} from '~/components/providers/MeasurementsProvider';

type EditSection = React.FC<{
  title?: string;
  children: React.ReactNode;
  className?: string;
}>;

const EditSection: EditSection = ({ title, children, className }) => (
  <VStack className={`gap-3 ${className ?? ''}`}>
    {title ? (
      <Heading size="md" className="ml-4 text-typography-700">
        {title}
      </Heading>
    ) : null}
    {children}
  </VStack>
);

const BOTTOM_BAR_HEIGHT = 72;
const NOT_SET_BADGE_CLASS = 'bg-warning-100';

export default function EditProfile() {
  const router = useRouter();
  const { t } = useTranslation();
  const { t: tDomain } = useTranslation('domain');
  const { getCommunityTranslation } = useCommunityTranslations();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { accountStatus } = useAuth();
  const { effectiveColorScheme } = useTheme();
  const queryClient = useQueryClient();
  const insets = useSafeAreaInsets();
  const isLargeScreen = useIsLargeScreen();
  const bottomBarExtraInset = isLargeScreen ? insets.bottom : 0;
  const blurTargetRef = useRef<View | null>(null);

  // Sheet states
  const [activeMultiSelect, setActiveMultiSelect] = useState<TagField | null>(null);
  const [isBirthDateOpen, setIsBirthDateOpen] = useState(false);
  const [isHeightOpen, setIsHeightOpen] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [activeTextField, setActiveTextField] = useState<'TextField1' | 'TextField2' | 'TextField3' | null>(null);
  const [isFirstNameOpen, setIsFirstNameOpen] = useState(false);
  const { weightUnit, setWeightUnit } = useMeasurementPreferences();

  // Fetch profile data
  const {
    data: profile,
    isPending: isLoadingProfile,
    error: profileError,
    refetch: refetchProfile,
    isFetching: isRefetchingProfile,
  } = useQuery({
    queryKey: ['editProfile'],
    queryFn: AccountService.getProfile,
  });

  // Fetch profile options
  const {
    data: profileOptions,
    isPending: isLoadingOptions,
    error: profileOptionsError,
    refetch: refetchProfileOptions,
    isFetching: isRefetchingProfileOptions,
  } = useQuery({
    queryKey: ['profileOptions'],
    queryFn: AccountService.getProfileOptions,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Local profile state for editing
  const [localProfile, setLocalProfile] = useState<IMemberProfileEdit | null>(null);

  // Initialize local profile when data loads
  React.useEffect(() => {
    if (profile && !localProfile) {
      setLocalProfile(profile);
    }
  }, [profile, localProfile]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: IMemberProfileEdit) => AccountService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editProfile'] });
      queryClient.invalidateQueries({ queryKey: ['member-details', accountStatus?.UserName] });
      showSuccessToast(t('editProfile.saveSuccess'));
      router.back();
    },
    onError: (error) => {
      showErrorToast(t('editProfile.saveError'), error);
    },
  });

  const updateField = useCallback(<K extends keyof IMemberProfileEdit>(
    field: K,
    value: IMemberProfileEdit[K]
  ) => {
    setLocalProfile((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  const translateTag = useCallback(
    (field: TagField, key: string): string => {
      const section = PROFILE_EDIT_TRANSLATION_MAP[field];
      if (!section) return key;
      const translationKey = `ProfileTags.${section}.${key}`;
      const translated = tDomain(translationKey, key);
      return translated === translationKey ? key : translated;
    },
    [tDomain]
  );

  const getSelectedDisplay = useCallback(
    (field: TagField): string | React.ReactNode => {
      if (!localProfile) return t('editProfile.notSet');
      const values = localProfile[field as keyof IMemberProfileEdit] as string[] | null | undefined;
      if (!values || values.length === 0) {
        return t('editProfile.notSet');
      }

      const label =
        values.length === 1
          ? translateTag(field, values[0])
          : t('search.multipleSelected', '{{count}} selected', { count: values.length });

      return (
        <Badge variant="solid" size="lg" action="muted" className="rounded-md bg-tertiary-500">
          <BadgeText className="normal-case font-bold color-white">{label}</BadgeText>
        </Badge>
      );
    },
    [localProfile, translateTag, t]
  );

  const optionsByField = useMemo(() => {
    const options = {} as Partial<Record<TagField, { key: string; label: string }[]>>;
    if (!profileOptions) return options;

    const fields: TagField[] = [
      'SocialTags', 'Genders', 'InterestedIn', 'Relationships', 'SexualInterests',
      'LookingFor', 'BodyTypes', 'BodyTypesLiked', 'BodyPartsLiked', 'BodyHair',
      'FacialHair', 'CommunityKinks', 'Kinks', 'Languages', 'Ethnicities',
    ];

    fields.forEach((field) => {
      const raw = profileOptions[field as keyof IProfileOptions] as string[] | null | undefined;
      if (!Array.isArray(raw) || raw.length === 0) {
        options[field] = [];
        return;
      }
      options[field] = raw.map((key) => ({ key, label: translateTag(field, key) }));
    });

    return options;
  }, [profileOptions, translateTag]);

  const activeOptions = useMemo(() => {
    if (!activeMultiSelect) return [];
    return optionsByField[activeMultiSelect] ?? [];
  }, [activeMultiSelect, optionsByField]);

  const activeSelectedKeys = useMemo(() => {
    if (!activeMultiSelect || !localProfile) return [];
    const values = localProfile[activeMultiSelect as keyof IMemberProfileEdit] as string[] | null | undefined;
    return values ?? [];
  }, [activeMultiSelect, localProfile]);

  const handleMultiSelectChange = useCallback(
    (field: TagField, keys: string[]) => {
      updateField(field as keyof IMemberProfileEdit, keys.length > 0 ? keys : null);
    },
    [updateField]
  );

  const handleSingleSelectChange = useCallback(
    (field: TagField, key: string | null) => {
      updateField(field as keyof IMemberProfileEdit, key ? [key] : null);
    },
    [updateField]
  );

  const handleSave = useCallback(() => {
    if (localProfile) {
      saveMutation.mutate(localProfile);
    }
  }, [localProfile, saveMutation]);

  const heightOptions = useMemo(() => {
    return HEIGHT_OPTIONS.map((h) => ({
      key: h.cm.toString(),
      label: h.display,
    }));
  }, []);

  const formatBirthDate = useCallback((date: string | null | undefined) => {
    if (!date) return t('editProfile.notSet');
    return dayjs(date).format('LL');
  }, [t]);

  const displayWeightValue = useCallback(
    (kg: number | null | undefined): string => {
      if (kg == null) return '';
      return String(Math.round(weightFromKg(kg, weightUnit)));
    },
    [weightUnit]
  );

  const parseWeightInput = useCallback(
    (text: string): number | null => {
      const num = parseInt(text, 10);
      if (isNaN(num)) return null;
      return weightToKg(num, weightUnit);
    },
    [weightUnit]
  );

  const weightUnitLabel = weightUnit === 'kg' ? 'KG' : weightUnit === 'st' ? 'ST' : 'LBS';

  const isWeightInvalid =
    localProfile?.WeightKg != null &&
    (localProfile.WeightKg < WEIGHT_MIN_KG || localProfile.WeightKg > WEIGHT_MAX_KG);
  const isWeightGoalInvalid =
    localProfile?.WeightGoalKg != null &&
    (localProfile.WeightGoalKg < WEIGHT_MIN_KG || localProfile.WeightGoalKg > WEIGHT_MAX_KG);

  const loadError = profileError ?? profileOptionsError;
  const isLoading = (isLoadingProfile || isLoadingOptions) && !loadError;
  const isSaving = saveMutation.isPending;
  const isRetryingLoad = isRefetchingProfile || isRefetchingProfileOptions;

  const handleRetryLoad = useCallback(() => {
    void refetchProfile();
    void refetchProfileOptions();
  }, [refetchProfile, refetchProfileOptions]);

  if (loadError) {
    return (
      <>
        <ScreenHeader options={{ title: t('editProfile.title') }} />
        <VStack className="flex-1 justify-center p-4">
          <ErrorInListView
            error={loadError}
            onRetry={handleRetryLoad}
            isRetrying={isRetryingLoad}
          />
        </VStack>
      </>
    );
  }

  if (isLoading || !localProfile) {
    return (
      <>
        <ScreenHeader options={{ title: t('editProfile.title') }} />
        <VStack className="flex-1 items-center justify-center p-4">
          <ButtonSpinner />
          <Text className="mt-2 text-typography-500">{t('common.loading')}</Text>
        </VStack>
      </>
    );
  }

  return (
    <>
      <ScreenHeader options={{ title: t('editProfile.title') }} />
      <BlurTargetView ref={blurTargetRef} style={{ flex: 1 }}>
        <ScrollView
          className="flex-1 bg-background-page"
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets
          contentContainerStyle={{ paddingBottom: BOTTOM_BAR_HEIGHT + bottomBarExtraInset }}>
          <VStack className="mx-auto w-full max-w-4xl gap-4 p-3 pt-4">
            {/* Basic Info Section */}
            <EditSection title={t('editProfile.sections.basicInfo')}>
              <Card className="gap-3" size="md">
                {/* First Name */}
                <SettingsItem
                  icon={EDIT_PROFILE_BASIC_ICONS.firstName}
                  label={t('editProfile.firstName')}
                  badge={localProfile.FirstName || t('editProfile.notSet')}
                  badgeClassName={!localProfile.FirstName ? NOT_SET_BADGE_CLASS : undefined}
                  onPress={() => setIsFirstNameOpen(true)}
                  hint={t('editProfile.hints.firstName')}
                />

                <Divider />

                {/* Birth Date */}
                <SettingsItem
                  icon={EDIT_PROFILE_BASIC_ICONS.birthDate}
                  label={t('editProfile.birthDate')}
                  badge={formatBirthDate(localProfile.BirthDate)}
                  badgeClassName={!localProfile.BirthDate ? NOT_SET_BADGE_CLASS : undefined}
                  onPress={() => setIsBirthDateOpen(true)}
                  hint={t('editProfile.hints.birthDate')}
                />
              </Card>
            </EditSection>

            {/* Stats Section */}
            <EditSection title={t('profile.stats')}>
              <Card className="gap-3" size="md">
                {/* Height */}
                <SettingsItem
                  icon={EDIT_PROFILE_BASIC_ICONS.height}
                  label={t('profile.height')}
                  badge={getHeightDisplay(localProfile.HeightCm) || t('editProfile.notSet')}
                  badgeClassName={!localProfile.HeightCm ? NOT_SET_BADGE_CLASS : undefined}
                  onPress={() => setIsHeightOpen(true)}
                />

                <Divider />

                {/* Weight */}
                <VStack className="gap-1">
                  <HStack className="items-center justify-between gap-2">
                    <HStack className="flex-1 items-center gap-2">
                      <HugeIconsWrapper
                        icon={EDIT_PROFILE_BASIC_ICONS.weight}
                        size={22}
                        className="color-typography-500"
                      />
                      <Text className="font-semibold">{t('profile.weight')}</Text>
                    </HStack>
                    <Input variant="outline" size="sm" className="w-24 bg-typography-0" isInvalid={isWeightInvalid}>
                      <InputField
                        placeholder="000"
                        value={displayWeightValue(localProfile.WeightKg)}
                        onChangeText={(text) => updateField('WeightKg', parseWeightInput(text))}
                        keyboardType="number-pad"
                        returnKeyType="done"
                        submitBehavior="blurAndSubmit"
                        onSubmitEditing={Keyboard.dismiss}
                        className="w-12 web:text-base"
                      />
                      {localProfile.WeightKg != null ? (
                        <InputSlot onPress={() => updateField('WeightKg', null)} className="pr-3">
                          <InputIcon as={CloseIcon} />
                        </InputSlot>
                      ) : null}
                    </Input>
                    <MenuSelectButton
                      selectedKey={weightUnit}
                      valueLabel={weightUnitLabel}
                      options={[
                        { key: 'lb', label: t('profile.units.weight.pounds') },
                        { key: 'kg', label: t('profile.units.weight.kilograms') },
                        { key: 'st', label: t('profile.units.weight.stones') },
                      ]}
                      onSelect={(key) => setWeightUnit(key as 'lb' | 'kg' | 'st')}
                      size="xs"
                      variant="outline"
                      action="neutral"
                      className="w-16 justify-center gap-1 px-2"
                      textClassName="text-typography-700"
                      iconClassName="color-typography-700"
                    />
                  </HStack>
                  {isWeightInvalid && (
                    <Text size="sm" className="text-error-500 ml-[30px]">
                      {t('editProfile.weightRangeError')}
                    </Text>
                  )}
                </VStack>

                {/* Weight Goal (if supported) */}
                {!isFieldUnsupported('WeightGoalKg', profileOptions) && (
                  <>
                    <Divider />
                    <VStack className="gap-1">
                      <HStack className="items-center justify-between gap-2">
                        <HStack className="flex-1 items-center gap-2">
                          <HugeIconsWrapper
                            icon={EDIT_PROFILE_BASIC_ICONS.weight}
                            size={22}
                            className="color-typography-500"
                          />
                          <Text className="font-semibold">{t('editProfile.weightGoal')}</Text>
                        </HStack>
                        <Input variant="outline" size="sm" className="w-24 bg-typography-0" isInvalid={isWeightGoalInvalid}>
                          <InputField
                            placeholder="000"
                            value={displayWeightValue(localProfile.WeightGoalKg)}
                            onChangeText={(text) => updateField('WeightGoalKg', parseWeightInput(text))}
                            keyboardType="number-pad"
                            returnKeyType="done"
                            submitBehavior="blurAndSubmit"
                            onSubmitEditing={Keyboard.dismiss}
                            className="w-12 web:text-base"
                          />
                          {localProfile.WeightGoalKg != null ? (
                            <InputSlot onPress={() => updateField('WeightGoalKg', null)} className="pr-3">
                              <InputIcon as={CloseIcon} />
                            </InputSlot>
                          ) : null}
                        </Input>
                        <MenuSelectButton
                          selectedKey={weightUnit}
                          valueLabel={weightUnitLabel}
                          options={[
                            { key: 'lb', label: t('profile.units.weight.pounds') },
                            { key: 'kg', label: t('profile.units.weight.kilograms') },
                            { key: 'st', label: t('profile.units.weight.stones') },
                          ]}
                          onSelect={(key) => setWeightUnit(key as 'lb' | 'kg' | 'st')}
                          size="xs"
                          variant="outline"
                          action="neutral"
                          className="w-16 justify-center gap-1 px-2"
                          textClassName="text-typography-700"
                          iconClassName="color-typography-700"
                        />
                      </HStack>
                      {isWeightGoalInvalid && (
                        <Text size="sm" className="text-error-500 ml-[30px]">
                          {t('editProfile.weightRangeError')}
                        </Text>
                      )}
                    </VStack>
                  </>
                )}
              </Card>
            </EditSection>

            {/* About Me / Text Fields Section */}
            <EditSection title={t('profile.aboutMe')}>
              <Card className="gap-3" size="md">
                {/* Bio / Description */}
                <SettingsItem
                  icon={EDIT_PROFILE_BASIC_ICONS.description}
                  label={t('profile.bio')}
                  badge={localProfile.Description || t('editProfile.notSet')}
                  badgeClassName={!localProfile.Description ? NOT_SET_BADGE_CLASS : undefined}
                  onPress={() => setIsDescriptionOpen(true)}
                  hint={t('editProfile.hints.bio')}
                />
                <Divider />
                {/* TextField1 */}
                <SettingsItem
                  icon={EDIT_PROFILE_BASIC_ICONS.textField1}
                  label={getCommunityTranslation('TextField1')}
                  badge={localProfile.TextField1 || t('editProfile.notSet')}
                  badgeClassName={!localProfile.TextField1 ? NOT_SET_BADGE_CLASS : undefined}
                  onPress={() => setActiveTextField('TextField1')}
                />
                <Divider />
                {/* TextField2 */}
                <SettingsItem
                  icon={EDIT_PROFILE_BASIC_ICONS.textField2}
                  label={getCommunityTranslation('TextField2')}
                  badge={localProfile.TextField2 || t('editProfile.notSet')}
                  badgeClassName={!localProfile.TextField2 ? NOT_SET_BADGE_CLASS : undefined}
                  onPress={() => setActiveTextField('TextField2')}
                />
                <Divider />
                {/* TextField3 */}
                <SettingsItem
                  icon={EDIT_PROFILE_BASIC_ICONS.textField3}
                  label={getCommunityTranslation('TextField3')}
                  badge={localProfile.TextField3 || t('editProfile.notSet')}
                  badgeClassName={!localProfile.TextField3 ? NOT_SET_BADGE_CLASS : undefined}
                  onPress={() => setActiveTextField('TextField3')}
                />
              </Card>
            </EditSection>

            {/* Multi-Select Sections */}
            {EDIT_PROFILE_SECTIONS.map((section) => {
              const visibleFields = section.fields.filter(
                (field) => (optionsByField[field]?.length ?? 0) > 0
              );
              if (visibleFields.length === 0) return null;

              return (
                <EditSection
                  key={section.key}
                  title={t(section.titleKey, section.fallbackTitle)}>
                  <Card className="gap-1" size="md">
                    {visibleFields.map((field, index) => {
                      const labelKey = MULTI_SELECT_LABEL_KEY_BY_FIELD[field] ?? '';
                      const values = localProfile[field as keyof IMemberProfileEdit] as string[] | null | undefined;
                      const isEmpty = !values || values.length === 0;
                      return (
                        <React.Fragment key={field}>
                          {index > 0 && <Divider />}
                          <SettingsItem
                            label={t(labelKey, field)}
                            badge={getSelectedDisplay(field)}
                            badgeClassName={isEmpty ? NOT_SET_BADGE_CLASS : undefined}
                            icon={EDIT_PROFILE_MULTI_SELECT_ICON_BY_FIELD[field] ?? DEFAULT_MULTI_SELECT_ICON}
                            onPress={() => setActiveMultiSelect(field)}
                          />
                        </React.Fragment>
                      );
                    })}
                  </Card>
                </EditSection>
              );
            })}

          </VStack>
        </ScrollView>
      </BlurTargetView>

      {/* Fixed Bottom Save Button - outside KeyboardAvoidingView */}
      <View className="absolute bottom-0 left-0 right-0">
        <SafeBlurView
          blurTarget={blurTargetRef}
          intensity={80}
          tint={effectiveColorScheme}
          blurMethod="dimezisBlurViewSdk31Plus"
          className="border-t border-outline-100"
          style={{ paddingBottom: bottomBarExtraInset }}>
          <HStack className="mx-auto w-full max-w-4xl gap-3 p-3">
            <Button className="flex-1" onPress={handleSave} isDisabled={isSaving || isWeightInvalid || isWeightGoalInvalid}>
              {isSaving && <ButtonSpinner className="mr-2" />}
              <ButtonText>
                {isSaving ? t('editProfile.saving') : t('common.saveChanges')}
              </ButtonText>
            </Button>
          </HStack>
        </SafeBlurView>
      </View>

      {/* Multi/Single Select Sheet */}
        <ContextSheet
          isOpen={activeMultiSelect !== null}
          onClose={() => setActiveMultiSelect(null)}
          contentComponent={isSingleSelectField(activeMultiSelect, profileOptions) ? SingleSelectSheet : MultiSelectSheet}
          title={
            activeMultiSelect
              ? t(MULTI_SELECT_LABEL_KEY_BY_FIELD[activeMultiSelect] ?? '', activeMultiSelect)
              : ''
          }
          payload={
            isSingleSelectField(activeMultiSelect, profileOptions)
              ? {
                  options: activeOptions,
                  selectedKey: activeSelectedKeys[0] ?? null,
                  onSelectionChange: (key: string | null) => {
                    if (activeMultiSelect) {
                      handleSingleSelectChange(activeMultiSelect, key);
                    }
                  },
                  renderOptionIcon: activeMultiSelect === 'SocialTags'
                    ? (key: string) => <View style={{ width: 28, alignItems: 'flex-end' }}><SocialTagIcon tag={key} size={20} /></View>
                    : undefined,
                }
              : {
                  options: activeOptions,
                  selectedKeys: activeSelectedKeys,
                  onSelectionChange: (keys: string[]) => {
                    if (activeMultiSelect) {
                      handleMultiSelectChange(activeMultiSelect, keys);
                    }
                  },
                  renderOptionIcon: activeMultiSelect === 'SocialTags'
                    ? (key: string) => <View style={{ width: 28, alignItems: 'flex-end' }}><SocialTagIcon tag={key} size={20} /></View>
                    : undefined,
                }
          }
        />

        {/* Birth Date Picker Sheet */}
        <ContextSheet
          isOpen={isBirthDateOpen}
          onClose={() => setIsBirthDateOpen(false)}
          contentComponent={DatePickerSheet}
          title={t('editProfile.birthDate')}
          payload={{
            selectedDate: localProfile.BirthDate,
            onDateChange: (date: Date) => {
              updateField('BirthDate', date.toISOString());
            },
          }}
        />

        {/* Height Picker Sheet */}
        <ContextSheet
          isOpen={isHeightOpen}
          onClose={() => setIsHeightOpen(false)}
          contentComponent={SingleSelectSheet}
          title={t('editProfile.selectHeight')}
          payload={{
            options: heightOptions,
            selectedKey: localProfile.HeightCm?.toString() ?? null,
            onSelectionChange: (key: string | null) => {
              updateField('HeightCm', key ? parseInt(key, 10) : null);
            },
            allowClear: true,
          }}
        />

        {/* Description/Bio Edit Sheet */}
        <ContextSheet
          isOpen={isDescriptionOpen}
          onClose={() => setIsDescriptionOpen(false)}
          contentComponent={TextEditSheet}
          title={t('profile.bio')}
          size="lg"
          height="lg"
          payload={{
            value: localProfile.Description,
            onSave: (text: string) => {
              updateField('Description', text || null);
            },
            placeholder: t('editProfile.descriptionPlaceholder'),
            maxLength: 40000,
          }}
        />

        {/* Text Field Edit Sheet */}
        <ContextSheet
          isOpen={activeTextField !== null}
          onClose={() => setActiveTextField(null)}
          contentComponent={TextEditSheet}
          title={activeTextField ? getCommunityTranslation(activeTextField) : ''}
          size="md"
          payload={{
            value: activeTextField ? localProfile[activeTextField] : '',
            onSave: (text: string) => {
              if (activeTextField) {
                updateField(activeTextField, text || null);
              }
            },
            placeholder: t('editProfile.textPlaceholder'),
            maxLength: 500,
          }}
        />

        {/* First Name Edit Sheet */}
        <ContextSheet
          isOpen={isFirstNameOpen}
          onClose={() => setIsFirstNameOpen(false)}
          contentComponent={TextEditSheet}
          title={t('editProfile.firstName')}
          height="xs"
          payload={{
            value: localProfile.FirstName,
            onSave: (text: string) => {
              updateField('FirstName', text.slice(0, 30) || null);
            },
            placeholder: t('editProfile.firstNamePlaceholder'),
            maxLength: 30,
            multiline: false,
            autoCorrect: false,
            autoCapitalize: 'words',
            disablePasswordManager: true,
          }}
        />
    </>
  );
}
